import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { useAuthStore } from '@/stores/auth'
import { i18n } from '@/i18n'
import { hasPermission } from '@/utils/utils'
import { Permission } from '@/constant'
import type { AuthUser } from '@/services/authServices'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        requiresAuth: true,
        title: 'nav.dashboard', // i18n key
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: {
        requiresAuth: false,
        title: 'auth.login', // i18n key
      },
    },
    {
      path: '/forgot_password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPasswordView.vue'),
      meta: {
        requiresAuth: false,
        title: 'auth.forgot.title', // i18n key
      },
    },
    {
      path: '/user/reset_password/:id/:token',
      name: 'reset-password',
      component: () => import('../views/ResetPasswordView.vue'),
      meta: {
        requiresAuth: false,
        title: 'auth.reset.title', // i18n key
      },
    },
    {
      path: '/email_sent',
      name: 'email-sent',
      component: () => import('../views/EmailSentView.vue'),
      meta: {
        requiresAuth: false,
        title: 'auth.sent.title', // i18n key
      },
    },
    {
      path: '/password_changed',
      name: 'password-changed',
      component: () => import('../views/PasswordChanged.vue'),
      meta: {
        requiresAuth: false,
        title: 'auth.sent.title', // i18n key
      },
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UsersView.vue'),
      meta: {
        requiresAuth: true,
        title: 'nav.users', // i18n key
        requiredPermission: [
            Permission.CREATE_OWN_CUSTOMER_USER,
          Permission.READ_OWN_CUSTOMER_USER,
          Permission.UPDATE_OWN_CUSTOMER_USER,
          Permission.DELETE_OWN_CUSTOMER_USER,
        ],
      },
    },
    {
      path: '/tags',
      name: 'tags',
      component: () => import('../views/TagsView.vue'),
      meta: {
        requiresAuth: true,
        title: 'nav.tags', // i18n key
        requiredPermission: [
          Permission.CREATE_TAG,
          Permission.READ_TAG,
          Permission.UPDATE_TAG,
          Permission.DELETE_TAG,
        ],
      },
    },
    {
      path: '/items',
      name: 'items-home',
      component: () => import('../views/ItemsHome.vue'),
      meta: {
        requiresAuth: true,
        title: 'nav.items', // i18n key
        requiredPermission: [
          Permission.CREATE_SET,
          Permission.READ_SET,
          Permission.UPDATE_SET,
          Permission.DELETE_SET,
          Permission.READ_ITEM,
        ],
      },
      children: [
        {
          path: '',
          name: 'items',
          component: () => import('../views/ItemsView.vue'),
        },
        {
          path: ':id',
          name: 'item-detail',
          component: () => import('../views/ItemDetail.vue'),
        },
        {
          path: 'sets',
          name: 'set-create',
          component: () => import('../views/SetCreate.vue'),
        },
      ],
    },
    {
      path: '/my-plans',
      name: 'my-plans',
      component: () => import('../views/MyPlansView.vue'),
      meta: {
        requiresAuth: true,
        title: 'nav.myPlans', // i18n key
      },
    },
    {
      path: '/my-plans/create',
      name: 'plan-create',
      component: () => import('../views/PlanFormView.vue'),
      meta: {
        requiresAuth: true,
        title: 'plan.form.tradeFairData', // i18n key
      },
    },
    {
      path: '/my-plans/:id/edit',
      name: 'plan-edit',
      component: () => import('../views/PlanFormView.vue'),
      meta: {
        requiresAuth: true,
        title: 'plan.form.tradeFairData', // i18n key
      },
    },
    {
      path: '/manual',
      name: 'manual',
      component: () => import('../views/ManualView.vue'),
      meta: {
        requiresAuth: true,
        title: 'manual.title', // i18n key
        // Deliberately no requiredPermission — the manual is open to every user.
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: {
        requiresAuth: false,
        title: 'Not Found', // or add to i18n
      },
    },
  ],
})

// Navigation guard to check authentication & authorization
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.meta.requiresAuth
  const requiredPermission = to.meta.requiredPermission as string | string[] | undefined

  // 1) Check authentication
  if (requiresAuth && !authStore.isAuthenticated) {
    // Route requires auth but user is not authenticated
    next({ name: 'login' })
    return
  }

  // 2) Prevent authenticated users from visiting login
  if (!requiresAuth && authStore.isAuthenticated && to.name === 'login') {
    // User is authenticated but trying to access login route
    next({ name: 'home' })
    return
  }

  // 3) Check permission (if route requires it). An admin-hub impersonation
  // session is granted access to all sections regardless of permissions.
  if (requiredPermission && !authStore.isAdminHubSession) {
    const user = authStore.user as AuthUser | null

    if (!hasPermission(user, requiredPermission)) {
      next({ name: 'home' })
      return
    }
  }

  // 4) Allow navigation
  next()
})

// Update document title after navigation
router.afterEach((to) => {
  const title = to.meta.title as string
  if (title) {
    // Check if it's an i18n key (contains a dot)
    const t = (i18n.global as { t: (key: string) => string }).t
    const pageTitle = title.includes('.') ? t(title) : title
    document.title = `ExhibitorHub - ${pageTitle}`
  } else {
    document.title = 'ExhibitorHub'
  }
})

export default router
